import { useLocalStorage } from '@uidotdev/usehooks';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { isTourMode } from 'tour/tourMode';
import { VIEW_PARAM_KEY } from 'utils/viewParam';
import { SAVED_VIEW_PARAM_KEY } from './filter-bar/builtInViews';

interface SavedFilterEntry {
    queryString: string;
    rememberFilter: boolean;
    /**
     * The user left the list with no filters, by Clear or by removing each one. An empty `queryString` alone also
     * means "nothing remembered yet", where the list opens on its defaults; this one opens on no filters.
     */
    isCleared?: boolean;
    customData?: Record<string, unknown>;
}

interface UseFilterPersistenceProps {
    canRememberFilters: boolean;
    initialSearchParams?: string;
}

/**
 * In the URL but not part of the filter state: never written to the saved entry, and a URL carrying
 * only these still counts as "no filters in the address", so saved filters are restored rather than
 * overridden.
 */
const TRANSIENT_PARAM_KEYS: readonly string[] = ['search', VIEW_PARAM_KEY, SAVED_VIEW_PARAM_KEY];

const normalizePathForFilterStorage = (pathname: string): string => {
    return pathname
        .split('/')
        .map((segment) => (/^\d+$/.test(segment) ? '{id}' : segment))
        .join('/');
};

const stripSearchParam = (queryString: string): string => {
    if (!queryString) return queryString;
    const cleanQueryString = queryString.startsWith('?') ? queryString.slice(1) : queryString;
    const params = new URLSearchParams(cleanQueryString);
    params.delete('search');
    const result = params.toString();
    return result ? `?${result}` : '';
};

export const useFilterPersistence = ({ canRememberFilters, initialSearchParams }: UseFilterPersistenceProps) => {
    const { pathname, search } = useLocation();
    const normalizedPath = useMemo(() => normalizePathForFilterStorage(pathname), [pathname]);

    const [savedFilters, setStoredFilters] = useLocalStorage<Record<string, SavedFilterEntry>>('list-filters', {});
    // The one writer: the guided tour's sample pages remember nothing.
    const setSavedFilters = useCallback<typeof setStoredFilters>(
        (value) => {
            if (!isTourMode()) setStoredFilters(value);
        },
        [setStoredFilters]
    );

    const { getInitialSearchParams, hasClearedFilters } = useMemo(() => {
        if (!pathname || !canRememberFilters) {
            return { getInitialSearchParams: initialSearchParams, hasClearedFilters: false };
        }

        const urlParams = new URLSearchParams(search);
        TRANSIENT_PARAM_KEYS.forEach((key) => urlParams.delete(key));
        const hasExplicitUrlParams = urlParams.toString().length > 0;

        // If URL has explicit filter params (mf, qf, sort, etc.), use them instead of saved filters
        if (hasExplicitUrlParams) {
            return { getInitialSearchParams: initialSearchParams || search, hasClearedFilters: false };
        }

        const savedEntry = savedFilters[normalizedPath];
        const shouldUseSavedFilters = savedEntry && savedEntry.rememberFilter;

        return shouldUseSavedFilters
            ? {
                  getInitialSearchParams: stripSearchParam(savedEntry.queryString),
                  hasClearedFilters: !!savedEntry.isCleared,
              }
            : { getInitialSearchParams: initialSearchParams, hasClearedFilters: false };
    }, [pathname, normalizedPath, search, savedFilters, initialSearchParams, canRememberFilters]);

    const isFilterPersistenceEnabled = useMemo(() => {
        if (!canRememberFilters) return false;
        return pathname ? (savedFilters[normalizedPath]?.rememberFilter ?? true) : true;
    }, [savedFilters, pathname, normalizedPath, canRememberFilters]);

    const toggleFilterPersistence = useCallback(
        (enabled: boolean) => {
            if (!pathname || !canRememberFilters) return;

            setSavedFilters((prev) => {
                const safePrev = prev ?? {};
                const currentEntry = safePrev[normalizedPath];
                return {
                    ...safePrev,
                    [normalizedPath]: {
                        queryString: currentEntry?.queryString || '',
                        rememberFilter: enabled,
                        ...(currentEntry?.isCleared ? { isCleared: true } : {}),
                    },
                };
            });
        },
        [pathname, normalizedPath, setSavedFilters, canRememberFilters]
    );

    /**
     * Saves filters to localStorage. Optionally accepts customData to save both in a single operation.
     *
     * IMPORTANT: When updating both filters and customData, pass customData as the second parameter
     * to avoid React state batching issues. Calling saveFilters() and saveCustomData() separately
     * in the same synchronous execution will cause the second call to overwrite changes from the first.
     */
    const saveFilters = useCallback(
        (searchParamObject: Record<string, string>, customData?: Record<string, unknown>) => {
            if (!pathname || !canRememberFilters) {
                return;
            }

            const persistableParams = Object.fromEntries(
                Object.entries(searchParamObject).filter(([key]) => !TRANSIENT_PARAM_KEYS.includes(key))
            );
            const queryString = new URLSearchParams(persistableParams).toString();
            const queryStringWithPrefix = queryString ? `?${queryString}` : '';

            setSavedFilters((prev) => {
                const safePrev = prev ?? {};
                const prevEntry = safePrev[normalizedPath];
                return {
                    ...safePrev,
                    [normalizedPath]: {
                        queryString: queryStringWithPrefix,
                        rememberFilter: true,
                        ...(queryStringWithPrefix ? {} : { isCleared: true }),
                        ...(customData !== undefined
                            ? { customData }
                            : prevEntry?.customData
                              ? { customData: prevEntry.customData }
                              : {}),
                    },
                };
            });
        },
        [pathname, normalizedPath, setSavedFilters, canRememberFilters]
    );

    const clearSavedFilters = useCallback(() => {
        if (!pathname || !canRememberFilters) return;

        setSavedFilters((prev) => {
            const safePrev = prev ?? {};
            const currentEntry = safePrev[normalizedPath];
            if (currentEntry) {
                return {
                    ...safePrev,
                    [normalizedPath]: {
                        ...currentEntry,
                        queryString: '',
                        isCleared: true,
                    },
                };
            }
            return safePrev;
        });
    }, [pathname, normalizedPath, setSavedFilters, canRememberFilters]);

    const initializeMissingEntry = useCallback(() => {
        if (!pathname || !canRememberFilters) {
            return;
        }

        setSavedFilters((prev) => {
            const safePrev = prev ?? {};
            if (safePrev[normalizedPath]) {
                return safePrev;
            }
            return {
                ...safePrev,
                [normalizedPath]: {
                    queryString: '',
                    rememberFilter: true,
                },
            };
        });
    }, [pathname, normalizedPath, setSavedFilters, canRememberFilters]);

    const getCustomData = useCallback(<T = Record<string, unknown>>(): T | undefined => {
        if (!pathname || !canRememberFilters) return undefined;
        return savedFilters[normalizedPath]?.customData as T | undefined;
    }, [pathname, normalizedPath, savedFilters, canRememberFilters]);

    const saveCustomData = useCallback(
        (data: Record<string, unknown>) => {
            if (!pathname || !canRememberFilters) {
                return;
            }

            setSavedFilters((prev) => {
                const safePrev = prev ?? {};
                const currentEntry = safePrev[normalizedPath];
                return {
                    ...safePrev,
                    [normalizedPath]: {
                        queryString: currentEntry?.queryString || '',
                        rememberFilter: currentEntry?.rememberFilter ?? true,
                        ...(currentEntry?.isCleared ? { isCleared: true } : {}),
                        customData: data,
                    },
                };
            });
        },
        [pathname, normalizedPath, setSavedFilters, canRememberFilters]
    );

    return {
        pathname,
        getInitialSearchParams,
        hasClearedFilters,
        isFilterPersistenceEnabled,
        toggleFilterPersistence,
        saveFilters,
        clearSavedFilters,
        initializeMissingEntry,
        getCustomData,
        saveCustomData,
    };
};
